import { CommonModule, DatePipe } from '@angular/common';
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  EventEmitter,
  inject,
  Input,
  Output,
} from '@angular/core';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { NzAvatarModule } from 'ng-zorro-antd/avatar';
import { NzButtonModule } from 'ng-zorro-antd/button';
import { NzIconModule } from 'ng-zorro-antd/icon';
import { NzTagModule } from 'ng-zorro-antd/tag';
import { NzToolTipModule } from 'ng-zorro-antd/tooltip';
import { NzModalService } from 'ng-zorro-antd/modal';
import {
  GenderPipe,
  heldPackageCounts,
  ServicePackageService,
  User,
  VisitCommentService,
} from '@beautyapp/core';
import {
  BirthdayBadgeComponent,
  BirthdaySendPanelComponent,
  Customer360CustomFieldsComponent,
  Customer360PhotosComponent,
} from '@beautyapp/customer-360';

import { UserBalanceModalComponent } from '../user-balance-modal/user-balance-modal.component';

/**
 * Modernized "Overview" panel that replaces the legacy two-column
 * information tab inside `UserViewComponent`. It consolidates the
 * profile card, KPI chips, contact details, customer custom fields
 * and visit photos into one focused tab. Balance management is no
 * longer inline — it's reached through a dedicated modal
 * (`UserBalanceModalComponent`) to reduce tab clutter.
 *
 * Lives in `libs/users` (rather than `libs/customer-360`) so the
 * dependency direction stays one-way (users → customer-360); the
 * inverse would create a cycle through `UserMinuteBalanceComponent`.
 */
@Component({
  selector: 'beautyapp-user-overview-panel',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    CommonModule,
    DatePipe,
    TranslatePipe,
    NzAvatarModule,
    NzButtonModule,
    NzIconModule,
    NzTagModule,
    NzToolTipModule,
    GenderPipe,
    Customer360CustomFieldsComponent,
    Customer360PhotosComponent,
    BirthdayBadgeComponent,
    BirthdaySendPanelComponent,
  ],
  template: `
    <div class="overview">
      <!-- HERO -->
      <section class="hero">
        <div class="hero__main">
          <div class="hero__id">
            <nz-avatar
              class="hero__avatar"
              nzSize="large"
              nzIcon="user"
            ></nz-avatar>
            <div>
              <div class="hero__name">
                {{ user.firstname }} {{ user.lastname }}
                <button
                  nz-button
                  nzSize="small"
                  nzShape="circle"
                  nz-tooltip="{{ 'buttons.edit' | translate }}"
                  (click)="edit.emit(user)"
                >
                  <span nz-icon nzType="edit"></span>
                </button>
                <button
                  nz-button
                  nzSize="small"
                  nzShape="circle"
                  [class.verified]="user.isVerified"
                  [class.unverified]="!user.isVerified"
                  nz-tooltip="{{
                    (user.isVerified ? 'users.verified' : 'users.verifyUser')
                      | translate
                  }}"
                  (click)="verify.emit(user)"
                >
                  <span nz-icon nzType="safety-certificate"></span>
                </button>
              </div>
              <div class="hero__meta">
                <nz-tag>{{ 'USER_ROLE.' + user.role | translate }}</nz-tag>
                @if (user.userSource?.name) {
                <nz-tag nzColor="blue">{{ user.userSource?.name }}</nz-tag>
                }
                <span class="hero__since">
                  <span nz-icon nzType="calendar"></span>
                  {{ user.createdAt | date : 'dd MMM yyyy' }}
                </span>
              </div>
            </div>
          </div>

          <dl class="hero__metrics">
            <div class="metric">
              <dt class="metric__label">
                {{ 'calendar.form.debt' | translate }}
              </dt>
              <dd
                class="metric__value"
                [class.metric__value--neg]="+user.debt < 0"
              >
                {{ user.debt | number : '1.2-2' }} ₾
              </dd>
            </div>
            <div class="metric">
              <dt class="metric__label">
                {{ 'calendar.form.balance' | translate }}
              </dt>
              <dd
                class="metric__value"
                [class.metric__value--pos]="+user.balance > 0"
              >
                {{ user.balance | number : '1.2-2' }} ₾
              </dd>
            </div>
            <div class="metric">
              <dt class="metric__label">
                {{ 'calendar.form.minutes' | translate }}
              </dt>
              <dd class="metric__value">
                {{ user.minutes }} {{ 'users.min' | translate }}
              </dd>
            </div>
            <div class="metric">
              <dt class="metric__label">
                {{ 'users.column.cashback' | translate }}
              </dt>
              <dd class="metric__value">
                {{ user.cashback | number : '1.2-2' }} ₾
              </dd>
            </div>
            <!-- beaut-497. Absent, not zero, when the customer holds none:
                 a "0" among four money figures reads as a balance of nothing
                 rather than as "this concept does not apply here".
                 Two figures, because "how many packages" and "how many were
                 gifts" are different questions and one number answers
                 neither on its own. -->
            @if (ownPackages + giftPackages > 0) {
            <div class="metric metric--action" data-test="service-packages">
              <dt class="metric__label">
                {{ 'users.servicePackages' | translate }}
              </dt>
              <dd class="metric__value">
                <button
                  type="button"
                  class="metric__link"
                  data-test="service-packages-link"
                  (click)="openGiftPackages.emit()"
                >
                  <span
                    nz-icon
                    nzType="appstore"
                    nz-tooltip
                    [nzTooltipTitle]="'users.ownPackages' | translate"
                  ></span>
                  <span data-test="own-packages">{{ ownPackages }}</span>
                  <span class="metric__sep">/</span>
                  <span
                    nz-icon
                    nzType="gift"
                    nz-tooltip
                    [nzTooltipTitle]="'users.giftPackages' | translate"
                  ></span>
                  <span data-test="gift-packages">{{ giftPackages }}</span>
                </button>
              </dd>
            </div>
            }
          </dl>

          <button
            nz-button
            nzType="primary"
            class="hero__cta"
            (click)="openBalanceModal()"
          >
            <span nz-icon nzType="plus-circle"></span>
            {{ 'users.upBalance' | translate }}
          </button>

          @if (user.description) {
          <div class="hero__note">
            <div class="hero__note-label">
              {{ 'users.form.additionalInformation' | translate }}
            </div>
            <div class="hero__note-text">{{ user.description }}</div>
          </div>
          }

          <div class="hero__note">
            <div class="hero__note-label">ბოლო კომენტარი</div>
            <div class="hero__note-text">
              @if (latestComment?.text) {
                @if (latestComment?.isPinned) {
                  <i nz-icon nzType="pushpin" nzTheme="fill" style="color: #faad14;"></i>
                }
                {{ latestComment?.text }}
              } @else {
                <span class="text-gray-400">კომენტარი არ არის</span>
              }
            </div>
          </div>
        </div>

        <div class="hero__info">
          <div class="hero__info-title">
            {{ 'users.information' | translate }}
          </div>
          <div class="details__grid">
            <div class="details__row">
              <span class="details__label">{{
                'users.form.idNumber' | translate
              }}</span>
              <span class="details__value">{{
                user.identityNumber || '—'
              }}</span>
            </div>
            <div class="details__row">
              <span class="details__label">{{
                'users.form.gender' | translate
              }}</span>
              <span class="details__value">{{
                (user.gender | gender) || '—'
              }}</span>
            </div>
            <div class="details__row">
              <span class="details__label">{{
                'users.form.dateOfBirth' | translate
              }}</span>
              <span class="details__value details__value--with-badge">
                {{ (user.birthDate | date : 'dd/MM/yyyy') || '—' }}
                <beautyapp-birthday-badge
                  [birthDate]="user.birthDate"
                ></beautyapp-birthday-badge>
              </span>
            </div>
            <div class="details__row">
              <span class="details__label">{{
                'users.form.homePhone' | translate
              }}</span>
              <span class="details__value">{{ user.phone || '—' }}</span>
            </div>
            <div class="details__row">
              <span class="details__label">{{
                'users.form.phoneNumber' | translate
              }}</span>
              <span class="details__value">{{ user.mobilePhone || '—' }}</span>
            </div>
            <div class="details__row">
              <span class="details__label">{{
                'users.form.email' | translate
              }}</span>
              <span class="details__value">{{ user.email || '—' }}</span>
            </div>
            <div class="details__row">
              <span class="details__label">{{
                'users.form.cardNo' | translate
              }}</span>
              <span class="details__value">{{ user.cardNo || '—' }}</span>
            </div>
            <div class="details__row">
              <span class="details__label">{{
                'location.city' | translate
              }}</span>
              <span class="details__value">{{ user.city || '—' }}</span>
            </div>
            <div class="details__row">
              <span class="details__label">{{
                'SHARED.ADDRESS' | translate
              }}</span>
              <span class="details__value">{{ user.address || '—' }}</span>
            </div>
          </div>

          <!--
            beaut-308: only when the customer actually has a birth date —
            a badge or a send control next to a blank date would imply one
            exists. Placed in the hero so it is visible without scrolling
            past the card, but separated from the details list by its own
            hairline so it does not read as one more detail row.
          -->
          @if (user.birthDate) {
            <div class="hero__birthday" data-test="birthday-greeting-section">
              <div class="hero__info-title">
                {{ 'calendar.form.birthdayGreeting' | translate }}
              </div>
              <beautyapp-birthday-send-panel
                [userId]="user.id"
              ></beautyapp-birthday-send-panel>
            </div>
          }
        </div>
      </section>

      <!-- CUSTOM FIELDS -->
      <section class="card">
        <header class="card__header">
          <h3>{{ 'users.preferencesAndNotes' | translate }}</h3>
        </header>
        <beautyapp-customer-360-custom-fields
          [userId]="user.id"
        ></beautyapp-customer-360-custom-fields>
      </section>

      <!-- PHOTOS -->
      <section class="card">
        <header class="card__header">
          <h3>{{ 'users.visitPhotos' | translate }}</h3>
        </header>
        <beautyapp-customer-360-photos
          [userId]="user.id"
        ></beautyapp-customer-360-photos>
      </section>
    </div>
  `,
  styles: [
    `
      :host {
        display: block;
      }

      .overview {
        display: flex;
        flex-direction: column;
        gap: 10px;
        padding: 2px 2px 12px;
      }

      /* Hero — two columns. Left: identity, the key numbers, then the balance
         button under them. Right: the Information details, divided by a hairline.
         Flat surface, no shadow. */
      .hero {
        display: grid;
        grid-template-columns: minmax(0, 1fr) minmax(0, 1fr);
        padding: 14px 18px;
        border-radius: 13px;
        background: #ffffff;
        border: 1px solid #e5e7eb;
      }
      .hero__main {
        display: flex;
        flex-direction: column;
        min-width: 0;
        padding-right: 28px;
      }
      .hero__id {
        display: flex;
        align-items: center;
        gap: 12px;
        min-width: 0;
      }
      .hero__avatar {
        width: 46px;
        height: 46px;
        line-height: 46px;
        font-size: 21px;
        background: #2563eb;
        flex-shrink: 0;
      }
      .hero__name {
        font-size: 17px;
        font-weight: 600;
        color: #0f172a;
        display: flex;
        align-items: center;
        gap: 8px;
        flex-wrap: wrap;
      }
      .hero__name button {
        border-color: #cbd5e1;
      }
      .hero__name button.verified {
        color: #16a34a;
        border-color: #86efac;
        background: #f0fdf4;
      }
      .hero__name button.unverified {
        color: #94a3b8;
      }
      .hero__meta {
        margin-top: 4px;
        display: flex;
        align-items: center;
        gap: 8px;
        flex-wrap: wrap;
        color: #64748b;
        font-size: 12.5px;
      }
      .hero__since {
        display: inline-flex;
        align-items: center;
        gap: 4px;
        color: #64748b;
      }
      .hero__cta {
        align-self: flex-start;
        margin-top: 14px;
        border-radius: 10px;
        height: 38px;
        font-weight: 500;
      }

      /* Key numbers as plain label/value pairs, divided from the identity above.
         Debt reads red only when owed, balance green only when positive. In the
         narrower left column the four wrap to a 2x2 grid. */
      .hero__metrics {
        display: flex;
        flex-wrap: wrap;
        gap: 10px 28px;
        margin: 12px 0 0;
        padding-top: 12px;
        border-top: 1px solid #eef0f3;
      }

      /* Right column: the Information details, separated by a vertical hairline. */
      .hero__info {
        min-width: 0;
        padding-left: 28px;
        border-left: 1px solid #eef0f3;
      }
      .hero__info-title {
        font-size: 11px;
        font-weight: 600;
        text-transform: uppercase;
        letter-spacing: 0.5px;
        color: #64748b;
        margin-bottom: 8px;
      }
      .metric {
        display: flex;
        flex-direction: column;
        gap: 2px;
        min-width: 84px;
      }
      .metric__label {
        font-size: 11px;
        text-transform: uppercase;
        letter-spacing: 0.4px;
        color: #64748b;
      }
      .metric__value {
        margin: 0;
        font-size: 16px;
        font-weight: 600;
        color: #0f172a;
        line-height: 1.15;
      }
      .metric__value--neg {
        color: #dc2626;
      }
      .metric__value--pos {
        color: #059669;
      }

      /* beaut-497. The gift count is the only metric that goes somewhere, so
         it has to look clickable while still sitting on the same baseline as
         the four money figures beside it — a real <button>, stripped of its
         chrome and given back the metric's own typography. Keeping it a button
         (not a styled span) is what carries keyboard focus and Enter. */
      .metric__link {
        margin: 0;
        padding: 0;
        border: 0;
        background: none;
        font: inherit;
        font-size: 16px;
        font-weight: 600;
        line-height: 1.15;
        color: #0f172a;
        cursor: pointer;
      }
      .metric__sep {
        margin: 0 4px;
        color: #cbd5e1;
      }
      .metric__link:hover,
      .metric__link:focus-visible {
        color: #2563eb;
        text-decoration: underline;
      }

      /* Additional info, under the balance button in the left column. */
      .hero__note {
        margin-top: 14px;
        padding-top: 12px;
        border-top: 1px solid #eef0f3;
      }
      .hero__note-label {
        font-size: 11px;
        font-weight: 600;
        text-transform: uppercase;
        letter-spacing: 0.5px;
        color: #64748b;
        margin-bottom: 4px;
      }
      .hero__note-text {
        font-size: 13px;
        color: #344054;
        white-space: pre-wrap;
        word-break: break-word;
      }

      /* Sections — flat, divided by a hairline instead of card chrome. Content
         aligns with the hero's inner padding (18px) though the box is invisible. */
      /* Override the global .card (bg + rounded + shadow-card) so these read as
         flat sections, divided by a hairline rather than boxed and shadowed. */
      .card {
        padding: 14px 18px 4px;
        background: transparent;
        border: none;
        border-top: 1px solid #eef0f3;
        border-radius: 0;
        box-shadow: none;
        overflow: visible;
      }
      .card__header {
        margin-bottom: 10px;
      }
      .card__header h3 {
        margin: 0;
        font-size: 11px;
        font-weight: 600;
        text-transform: uppercase;
        letter-spacing: 0.5px;
        color: #64748b;
      }

      /* Details — single column inside the hero's right info panel. */
      .details__grid {
        display: grid;
        grid-template-columns: 1fr;
        gap: 0;
      }
      .details__row {
        display: flex;
        justify-content: space-between;
        align-items: center;
        gap: 12px;
        padding: 5px 0;
        min-width: 0;
      }
      .details__label {
        font-size: 12.5px;
        color: #64748b;
        flex-shrink: 0;
      }
      .details__value {
        font-size: 13.5px;
        color: #0f172a;
        font-weight: 500;
        text-align: right;
        overflow: hidden;
        text-overflow: ellipsis;
        white-space: nowrap;
      }
      /* The birth-date row is the one place a badge can trail the value —
         let it wrap onto its own line rather than fight the ellipsis above. */
      .details__value--with-badge {
        white-space: normal;
        text-overflow: clip;
      }

      /* Send control lives beside the fact it acts on, separated from the
         details list above by its own hairline so it doesn't read as one
         more detail row. Only rendered when the customer has a birth date. */
      .hero__birthday {
        margin-top: 14px;
        padding-top: 12px;
        border-top: 1px solid #eef0f3;
      }

      @media (max-width: 900px) {
        .hero {
          grid-template-columns: 1fr;
        }
        .hero__main {
          padding-right: 0;
        }
        .hero__info {
          padding-left: 0;
          border-left: none;
          margin-top: 14px;
          padding-top: 14px;
          border-top: 1px solid #eef0f3;
        }
        .details__grid {
          grid-template-columns: 1fr;
        }
      }
    `,
  ],
})
export class UserOverviewPanelComponent {
  private _user!: User;
  // Fetch the customer's pinned-else-latest comment when the user changes.
  // One call per customer, guarded by id (no N+1); markForCheck for OnPush.
  @Input({ required: true }) set user(value: User) {
    this._user = value;
    const id = value?.id;
    if (id && id !== this.lastCommentUserId) {
      this.lastCommentUserId = id;
      this.visitCommentService.getCustomerLatestComment(id).subscribe((res) => {
        this.latestComment = res;
        this.cdr.markForCheck();
      });
      this.loadPackageCounts(id);
    }
  }
  get user(): User {
    return this._user;
  }

  @Output() edit = new EventEmitter<User>();
  @Output() verify = new EventEmitter<User>();
  @Output() refresh = new EventEmitter<void>();
  /** beaut-497. The host owns the tabs, so it decides where this lands. */
  @Output() openGiftPackages = new EventEmitter<void>();

  latestComment: { text: string; isPinned: boolean } | null = null;
  private lastCommentUserId: string | null = null;

  /** Active packages this customer bought for themselves. */
  ownPackages = 0;

  /** Active packages somebody else bought for them. */
  giftPackages = 0;

  /** Counted by the shared `heldPackageCounts` — the visit card shows the
   * same figure, and the reasoning behind the two requests lives there. */
  private loadPackageCounts(userId: string): void {
    heldPackageCounts(this.packageService, userId).subscribe(({ own, gift }) => {
      this.ownPackages = own;
      this.giftPackages = gift;
      this.cdr.markForCheck();
    });
  }

  private modal = inject(NzModalService);
  private translate = inject(TranslateService);
  private visitCommentService = inject(VisitCommentService);
  private packageService = inject(ServicePackageService);
  private cdr = inject(ChangeDetectorRef);

  openBalanceModal(): void {
    const ref = this.modal.create({
      nzTitle: this.translate.instant('users.upBalance'),
      nzContent: UserBalanceModalComponent,
      nzWidth: '760px',
      nzData: { user: this.user },
      nzFooter: null,
    });
    // Always refetch on close. The minutes tab can mutate the balance
    // without closing the modal (it triggers `ok` on every change), and
    // the amount tab closes with `true` after a successful save — in
    // either path the parent needs the freshest user snapshot, so we
    // don't branch on the close result.
    ref.afterClose.subscribe(() => this.refresh.emit());
  }
}
